import {Trans} from '@lingui/macro';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {isValidEmailAddress} from '../../../../../libs/shared/helpers/src/utilities/stringHelpers';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {AlertDialog} from '../../../../components/common/dialogs/alertDialog';
import {InputDialog} from '../../../../components/common/dialogs/inputDialog';
import {ChannelModel} from '../../../../models/api/channelModel';
import {postAsync} from '../../../../services/http/http';
import {SettingsBannersEnum, useSettingsBanners} from '../../../banners/settingsBannersContext';
import {StyledSettingsFormTextBox} from '../../../common/settingsWrapper';
import {SettingsCommonChannelFieldProps} from '../settingsCommonChannelFields';

/*
 * Props.
 */

interface SettingsAddressFieldProps extends SettingsCommonChannelFieldProps<ChannelModel> {
  isDisabled?: boolean;
}

/*
 * Style.
 */

const StyledAddressWrapperDiv = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: auto;
  grid-gap: 10px;
  align-items: center;
  justify-items: start;
  justify-content: start;
`;

const StyledEditedAddressButton = styled(Button)`
  margin-top: 13px;
`;

/*
 * Component.
 */

export const SettingsAddressField: FC<SettingsAddressFieldProps> = (props) => {
  const {edited} = props;
  const {showBanner} = useSettingsBanners();

  const [isInputDialogOpen, setIsInputDialogOpen] = useState(false);
  const [newSendAs, setNewSendAs] = useState('');
  const [isAlertOpen, setIsAlertOpen] = useState(false);

  const requestChangeSendAs = async (value: string) => {
    try {
      await postAsync(`${edited.url}/change_send_as`, {send_as: value});
    } catch (error) {
      showBanner({type: SettingsBannersEnum.ERROR, message: error.data.message});
    }

    setIsInputDialogOpen(false);
    setIsAlertOpen(true);
    setNewSendAs(value);
  };

  const maybeRenderDialog = () => {
    if (isAlertOpen) {
      return (
        <AlertDialog
          title={<Trans id="settingschannel-address-title">Send as</Trans>}
          onOkClick={() => setIsAlertOpen(false)}
          onRequestClose={() => setIsAlertOpen(false)}
        >
          <Trans id="settingschannel-address-copy">
            We have sent a verification email to <strong>{newSendAs}</strong>. We will continue to use your
            previous address until you can confirm the new one.
          </Trans>
        </AlertDialog>
      );
    }

    if (isInputDialogOpen) {
      return (
        <InputDialog
          title={<Trans id="settingschannel-address-title">Send as</Trans>}
          checkIfOkIsDisabled={(value) => !isValidEmailAddress(value)}
          label={<Trans id="settingschannel-address-title">Send as</Trans>}
          okButtonLabel={<Trans id="settingschannel-changeaddress">Change address</Trans>}
          onValueSelect={requestChangeSendAs}
          onRequestClose={() => setIsInputDialogOpen(false)}
        >
          <Trans id="settingschannel-changeaddress-copy">
            Your emails are sent from <strong>{edited.address}</strong>.
          </Trans>
          <br />
          <Trans id="settingschannel-changeaddress-copy2">
            You can configure Front to send emails from another address you own.
          </Trans>
          <br />
        </InputDialog>
      );
    }

    return null;
  };

  return (
    <>
      {maybeRenderDialog()}
      <StyledAddressWrapperDiv>
        <StyledSettingsFormTextBox
          $width={300}
          size={VisualSizesEnum.MEDIUM}
          value={edited.sendAddress}
          label={<Trans id="settingschannel-sendaddress">Address</Trans>}
          isDisabled={props.isDisabled}
        />
        {!props.isDisabled && (
          <StyledEditedAddressButton
            onClick={() => {
              setIsInputDialogOpen(true);
            }}
            variant={ButtonVariantsEnum.SECONDARY}
            size={VisualSizesEnum.SMALL}
          >
            <Trans id="settingschannel-edit">Edit</Trans>
          </StyledEditedAddressButton>
        )}
      </StyledAddressWrapperDiv>
    </>
  );
};
